<script setup>
import { computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * LaisPermissionState — calm visual gate for access-blocked surfaces.
 *
 * Renders in place of protected content when the viewer is not allowed to see
 * it: permission denied, a feature flag is off, the plan lacks the resource,
 * or the viewer is unauthenticated. It is a *replacement*, not an overlay — it
 * NEVER renders the gated content, so no sensitive data leaks through a slot.
 * This is a status region (calm), not an error/alert. Neutral + primary tokens
 * only; unknown variants fall back to `denied`.
 */
const props = defineProps({
  variant: {
    type: String,
    default: 'denied',
    validator: value =>
      ['denied', 'feature-off', 'plan', 'unauthenticated'].includes(value),
  },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  actionLabel: { type: String, default: '' },
  actionHref: { type: String, default: '' },
  showAction: { type: Boolean, default: false },
});

const emit = defineEmits(['action']);

const VARIANTS = Object.freeze([
  'denied',
  'feature-off',
  'plan',
  'unauthenticated',
]);

const VARIANT_ICONS = Object.freeze({
  denied: 'i-lucide-lock',
  'feature-off': 'i-lucide-toggle-left',
  plan: 'i-lucide-badge-dollar-sign',
  unauthenticated: 'i-lucide-log-in',
});

const { t } = useI18n();
const instance = getCurrentInstance();
const baseId = `lais-permission-state-${instance?.uid || 'default'}`;
const titleId = `${baseId}-title`;
const descriptionId = `${baseId}-description`;

const resolvedVariant = computed(() =>
  VARIANTS.includes(props.variant) ? props.variant : 'denied'
);

const variantIcon = computed(() => VARIANT_ICONS[resolvedVariant.value]);

// Literal i18n keys per variant (no dynamic keys) so the linter can verify the
// catalog. Each variant overrides the generic LAIS_PERMISSION_STATE fallback.
const variantCopy = computed(() => ({
  denied: {
    title: t('LAIS_PERMISSION_STATE.DENIED.TITLE'),
    description: t('LAIS_PERMISSION_STATE.DENIED.DESCRIPTION'),
  },
  'feature-off': {
    title: t('LAIS_PERMISSION_STATE.FEATURE_OFF.TITLE'),
    description: t('LAIS_PERMISSION_STATE.FEATURE_OFF.DESCRIPTION'),
  },
  plan: {
    title: t('LAIS_PERMISSION_STATE.PLAN.TITLE'),
    description: t('LAIS_PERMISSION_STATE.PLAN.DESCRIPTION'),
  },
  unauthenticated: {
    title: t('LAIS_PERMISSION_STATE.UNAUTHENTICATED.TITLE'),
    description: t('LAIS_PERMISSION_STATE.UNAUTHENTICATED.DESCRIPTION'),
  },
}));

const resolvedTitle = computed(
  () => props.title || variantCopy.value[resolvedVariant.value].title
);

const resolvedDescription = computed(
  () =>
    props.description || variantCopy.value[resolvedVariant.value].description
);

const resolvedActionLabel = computed(() => {
  if (props.actionLabel) return props.actionLabel;
  return props.showAction ? t('LAIS_PERMISSION_STATE.ACTION') : '';
});

const hasAction = computed(() => resolvedActionLabel.value.length > 0);

const actionTag = computed(() => (props.actionHref ? 'a' : 'button'));

const describedBy = computed(() =>
  resolvedDescription.value ? descriptionId : undefined
);

const handleAction = event => {
  emit('action', event);
};
</script>

<template>
  <section
    role="status"
    :aria-labelledby="titleId"
    :aria-describedby="describedBy"
    :data-variant="resolvedVariant"
    class="grid min-h-[360px] w-full place-items-center gap-6 rounded-lais border border-lais-border-soft bg-lais-bg-card px-4 py-10 text-center text-lais-text shadow-lais-soft sm:px-6"
  >
    <div class="flex max-w-[32rem] flex-col items-center gap-4">
      <div
        class="grid size-20 place-items-center rounded-lais-circle border border-lais-border-soft bg-lais-bg-soft text-lais-icon sm:size-24"
      >
        <span
          class="size-9 sm:size-10"
          :class="variantIcon"
          aria-hidden="true"
        />
      </div>
      <div class="min-w-0">
        <h2
          :id="titleId"
          class="text-pretty font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
        >
          {{ resolvedTitle }}
        </h2>
        <p
          v-if="resolvedDescription"
          :id="descriptionId"
          class="mt-2 text-balance font-redHatText text-lais-body-md text-lais-text-body"
        >
          {{ resolvedDescription }}
        </p>
      </div>
      <component
        :is="actionTag"
        v-if="hasAction"
        :href="actionHref || undefined"
        :type="actionHref ? undefined : 'button'"
        :aria-label="resolvedActionLabel"
        class="mt-1 inline-flex min-h-10 items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 py-2.5 font-redHatText text-lais-body-md font-semibold text-white shadow-lais-soft transition-colors hover:bg-lais-primary-600 focus-visible:outline-none focus-visible:shadow-lais-focus [.lais-dark_&]:text-lais-primary-deep"
        @click="handleAction"
      >
        <span>{{ resolvedActionLabel }}</span>
        <span class="i-lucide-arrow-right size-4 shrink-0" aria-hidden="true" />
      </component>
    </div>
  </section>
</template>
